const CANDLE_INTERVAL = "15m";
const CANDLE_MS = 15 * 60 * 1000;
const BINANCE_FUTURES_BASE = "https://fapi.binance.com/fapi/v1";
const SCORING_CONCURRENCY = 8;

const candleCache = new Map();

export function flattenHistoryEvents(history) {
  const out = [];
  const sections = Array.isArray(history?.sections) ? history.sections : [];

  sections.forEach((section) => {
    const sectionTitle = String(section?.title || "").trim();
    const events = Array.isArray(section?.events) ? section.events : [];
    if (!sectionTitle || !events.length) return;

    events.forEach((event) => {
      if (!event?.cur || !event?.ts) return;
      const direction = getDirection(event);
      if (!direction) return;
      out.push({
        ...event,
        sectionTitle,
        sequenceToken: buildSequenceToken(sectionTitle, event),
        tfToken: buildTfToken(sectionTitle, event),
        direction,
        ts: Number(event.ts),
      });
    });
  });

  return out.sort((a, b) => (a.ts || 0) - (b.ts || 0));
}

export function buildSequenceCandidates(events, options = {}) {
  const directionFilter = options.direction || "all";
  const maxGapSec = Number(options.maxGapHours || 24) * 60 * 60;
  const timeframeMode = options.timeframeMode || (options.sameTimeframeOnly === true ? "same" : "any");
  const byCoinDirection = new Map();

  events.forEach((event) => {
    if (directionFilter !== "all" && event.direction !== directionFilter) return;
    const key = `${event.cur}|${event.direction}`;
    if (!byCoinDirection.has(key)) byCoinDirection.set(key, []);
    byCoinDirection.get(key).push(event);
  });

  const groups = new Map();

  byCoinDirection.forEach((coinEvents) => {
    coinEvents.sort((a, b) => a.ts - b.ts);
    for (let i = 0; i < coinEvents.length; i += 1) {
      const first = coinEvents[i];
      for (let j = i + 1; j < coinEvents.length; j += 1) {
        const second = coinEvents[j];
        const gapSec = second.ts - first.ts;
        if (gapSec <= 0) continue;
        if (gapSec > maxGapSec) break;
        if (first.sequenceToken === second.sequenceToken) continue;
        if (timeframeMode === "same" && first.tfToken !== second.tfToken) continue;
        if (timeframeMode === "cross" && first.tfToken === second.tfToken) continue;

        const key = `${first.direction}|${first.sequenceToken} -> ${second.sequenceToken}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            direction: first.direction,
            label: `${first.sequenceToken} -> ${second.sequenceToken}`,
            occurrences: [],
            coins: new Set(),
          });
        }
        const group = groups.get(key);
        group.coins.add(first.cur);
        group.occurrences.push({
          cur: first.cur,
          direction: first.direction,
          first,
          second,
          gapSec,
        });
      }
    }
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      coinCount: group.coins.size,
      occurrences: group.occurrences.sort((a, b) => b.second.ts - a.second.ts),
    }))
    .sort((a, b) => {
      if (b.coinCount !== a.coinCount) return b.coinCount - a.coinCount;
      return b.occurrences.length - a.occurrences.length;
    });
}

export async function analyzePatterns(patterns, options = {}, onProgress) {
  const horizonHours = Number(options.horizonHours || 4);
  const maxExamplesPerPattern = Number(options.maxExamplesPerPattern || 12);
  let completed = 0;
  const patternJobs = patterns.map((pattern, patternIndex) => ({
    pattern,
    patternIndex,
    sampled: spreadSample(pattern.occurrences, maxExamplesPerPattern),
    examples: [],
  }));
  const jobs = patternJobs.flatMap((patternJob) =>
    patternJob.sampled.map((occurrence) => ({
      patternIndex: patternJob.patternIndex,
      occurrence,
    }))
  );
  const total = jobs.length;

  await mapWithConcurrency(jobs, SCORING_CONCURRENCY, async (job) => {
    const outcome = await scoreOccurrence(job.occurrence, horizonHours);
    completed += 1;
    if (onProgress) onProgress({ completed, total });
    if (outcome) {
      patternJobs[job.patternIndex].examples.push({ ...job.occurrence, outcome });
    }
  });

  const analyzed = patternJobs
    .filter((patternJob) => patternJob.examples.length > 0)
    .map((patternJob) => summarizePattern(patternJob.pattern, patternJob.examples, horizonHours));

  return analyzed.sort((a, b) => {
    if (b.avgCloseReturn !== a.avgCloseReturn) return b.avgCloseReturn - a.avgCloseReturn;
    if (b.winRate !== a.winRate) return b.winRate - a.winRate;
    return b.samples - a.samples;
  });
}

async function mapWithConcurrency(items, concurrency, iteratee) {
  let index = 0;
  const workerCount = Math.min(concurrency, items.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (index < items.length) {
      const currentIndex = index;
      index += 1;
      await iteratee(items[currentIndex], currentIndex);
    }
  });
  await Promise.all(workers);
}

export function openPatternExample(openPopup, pattern, occurrence, allEvents) {
  if (!openPopup || !occurrence?.second) return;
  const sourceOccurrences = Array.isArray(pattern.occurrences) && pattern.occurrences.length
    ? pattern.occurrences
    : pattern.examples || [];
  const sectionEvents = sourceOccurrences
    .map((item) => item.second)
    .filter(Boolean)
    .sort((a, b) => (b.ts || 0) - (a.ts || 0));
  const index = Math.max(
    0,
    sectionEvents.findIndex((event) => event.cur === occurrence.cur && event.ts === occurrence.second.ts)
  );

  openPopup({
    index,
    sectionEvents,
    sectionTitleWhenFromHistory: pattern.label,
    fullHistoryFlat: allEvents,
  });
}

function getDirection(event) {
  if (event?.bull && !event?.bear) return "long";
  if (event?.bear && !event?.bull) return "short";
  return null;
}

function buildSequenceToken(sectionTitle, event) {
  const title = String(sectionTitle || "")
    .replace(/\s*\(\d+\)\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
  const tf = formatTf(event?.tf);
  if (title) return title;
  return `${tf} ${event?.name || event?.cur || "EVENT"}`.trim();
}

function buildTfToken(sectionTitle, event) {
  const title = String(sectionTitle || "").toUpperCase();
  const matches = title.match(/\b(15M|4H|1D|1W|5M)\b/g);
  if (matches?.length) return [...new Set(matches)].join("-");
  return formatTf(event?.tf);
}

function formatTf(tf) {
  return String(tf || "")
    .replace(/^_/, "")
    .toUpperCase() || "NA";
}

function spreadSample(items, limit) {
  if (items.length <= limit) return items;
  const out = [];
  const step = (items.length - 1) / (limit - 1);
  for (let i = 0; i < limit; i += 1) {
    out.push(items[Math.round(i * step)]);
  }
  return out;
}

async function scoreOccurrence(occurrence, horizonHours) {
  const symbol = `${String(occurrence.cur || "").toUpperCase()}USDT`;
  const startMs = Math.max(0, occurrence.second.ts * 1000 - CANDLE_MS);
  const endMs = occurrence.second.ts * 1000 + horizonHours * 60 * 60 * 1000 + CANDLE_MS;
  const candles = await fetchKlines(symbol, startMs, endMs);
  if (!candles.length) return null;

  const entryIndex = candles.findIndex((candle) => candle.closeTimeSec >= occurrence.second.ts);
  if (entryIndex < 0) return null;
  const entryCandle = candles[entryIndex];
  const forward = candles.filter(
    (candle, index) => index > entryIndex && candle.openTimeMs <= endMs
  );
  if (!forward.length || !entryCandle.close) return null;

  const entry = entryCandle.close;
  const maxHigh = Math.max(...forward.map((candle) => candle.high));
  const minLow = Math.min(...forward.map((candle) => candle.low));
  const close = forward[forward.length - 1].close;
  const isLong = occurrence.direction === "long";
  const favorable = isLong
    ? percentMove(maxHigh, entry)
    : percentMove(entry, minLow);
  const adverse = isLong
    ? percentMove(minLow, entry)
    : percentMove(entry, maxHigh);
  const closeReturn = isLong
    ? percentMove(close, entry)
    : percentMove(entry, close);

  return {
    entry,
    close,
    favorable,
    adverse,
    closeReturn,
    win: closeReturn > 0,
  };
}

async function fetchKlines(symbol, startMs, endMs) {
  const cacheKey = `${symbol}:${CANDLE_INTERVAL}:${startMs}:${endMs}`;
  if (candleCache.has(cacheKey)) return candleCache.get(cacheKey);

  const url = `${BINANCE_FUTURES_BASE}/klines?symbol=${encodeURIComponent(symbol)}&interval=${CANDLE_INTERVAL}&startTime=${Math.floor(startMs)}&endTime=${Math.floor(endMs)}&limit=1000`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      candleCache.set(cacheKey, []);
      return [];
    }
    const raw = await response.json();
    const candles = Array.isArray(raw)
      ? raw.map((item) => ({
          openTimeMs: Number(item[0]),
          open: Number(item[1]),
          high: Number(item[2]),
          low: Number(item[3]),
          close: Number(item[4]),
          closeTimeSec: Math.floor(Number(item[6]) / 1000),
        })).filter((candle) => Number.isFinite(candle.close))
      : [];
    candleCache.set(cacheKey, candles);
    return candles;
  } catch {
    candleCache.set(cacheKey, []);
    return [];
  }
}

function summarizePattern(pattern, examples, horizonHours) {
  const closeReturns = examples.map((example) => example.outcome.closeReturn);
  const favorable = examples.map((example) => example.outcome.favorable);
  const adverse = examples.map((example) => example.outcome.adverse);
  const wins = examples.filter((example) => example.outcome.win).length;
  const medianPain = median(adverse);
  const avgFavorable = avg(favorable);
  const avgCloseReturn = avg(closeReturns);

  return {
    ...pattern,
    seenOccurrences: pattern.occurrences.length,
    seenCoinCount: pattern.coinCount,
    samples: examples.length,
    scoredCoinCount: new Set(examples.map((example) => example.cur)).size,
    horizonHours,
    winRate: wins / examples.length,
    avgFavorable,
    medianAdverse: medianPain,
    avgCloseReturn,
    expansionToPain: Math.abs(medianPain) > 0 ? avgFavorable / Math.abs(medianPain) : null,
    examples: examples.sort((a, b) => b.outcome.closeReturn - a.outcome.closeReturn),
  };
}

function avg(values) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function percentMove(to, from) {
  if (!from) return 0;
  return ((to - from) / from) * 100;
}
